import React from "react";
import { Box, Typography, Button } from "@mui/material";

/**
 * CreditBalanceCard
 * Settings-page card showing the org's current interpretation credit balance
 * and price per credit, with an action to top up.
 *
 * Props:
 *  - credits: number
 *  - pricePerCredit: number (in rupees)
 *  - onTopUpClick: () => void
 */

const CreditBalanceCard = ({
  credits = 0,
  isUnlimited = false,
  pricePerCredit = 49,
  canTopUp = true,
  onTopUpClick,
  // A migrated business's plan_entitlements can read as unlimited (a migration-time
  // stopgap, not a real Enterprise plan) - when isMigrated is true, show its actual
  // plan name (from subscription-logs) instead of an Unlimited/∞ balance.
  isMigrated = false,
  planName = null,
}) => {
  const isEmpty = !isUnlimited && !isMigrated && Number(credits) <= 0;

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #e2e0ed",
        borderRadius: "14px",
        width: "100%",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "12px",
          padding: "20px 22px",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#22223b" }}>
            {isMigrated ? (planName || "Your Plan") : "Interpretation credits"}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#77758a", marginTop: "2px" }}>
            {isMigrated
              ? "Managed by your account manager"
              : isUnlimited
                ? "Organisation-wide balance · Unlimited"
                : `Organisation-wide balance · ₹${pricePerCredit} per credit`}
          </Typography>
        </Box>

        {/* Disabled (not hidden) for plans that don't self-serve top up, e.g.
            Enterprise, whose credits are managed by their account manager.
            Migrated businesses never self-serve top up either. */}
        <Button
          onClick={onTopUpClick}
          variant="contained"
          disabled={!canTopUp || isMigrated}
          sx={{
            ...(isMigrated ? { display: "none" } : {}),
            backgroundColor: "#6c45ff",
            color: "#fff",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            borderRadius: "999px",
            padding: "8px 18px",
            whiteSpace: "nowrap",
            boxShadow: "none",
            flexShrink: 0,

            "&:hover": {
              backgroundColor: "#5d38eb",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              backgroundColor: "#EEECF9",
              color: "#9B96B8",
            },
          }}
        >
          + Top up credits
        </Button>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid #e2e0ed",
          padding: "20px 22px",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        {/* A migrated business's entitlements aren't a real credit balance (they're a
            migration-time stopgap), so there's no meaningful number to show here - the
            header above already names their actual plan. */}
        {!isMigrated && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
            <Typography
              sx={{
                fontSize: "40px",
                fontWeight: 700,
                lineHeight: 1,
                color: isEmpty ? "#D71920" : "#22223b",
              }}
            >
              {isUnlimited ? "∞" : credits}
            </Typography>
            <Box sx={{ pt: "6px" }}>
              <Typography sx={{ fontSize: "13px", color: "#66647a" }}>
                credits available
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#66647a" }}>
                shared across everyone in this organization
              </Typography>
            </Box>
          </Box>
        )}

        {isEmpty && (
          <Box
            sx={{
              backgroundColor: "#FDECEC",
              border: "1px solid #F3C8C8",
              borderRadius: "9px",
              padding: "12px 14px",
            }}
          >
            <Typography sx={{ fontSize: "13px", color: "#B3261E" }}>
              <strong>You have 0 credits.</strong> Reports cannot be sent for interpretation until
              you top up, and auto-send stays paused.
            </Typography>
          </Box>
        )}

        {/* <Box
          sx={{
            backgroundColor: "#F6F6F8",
            border: "1px solid #E5E4EA",
            borderRadius: "9px",
            padding: "12px 14px",
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#66647a" }}>
            Credits are pooled for the whole organisation, not per user.{" "}
            {canTopUp
              ? "Only a super admin can top up."
              : "Your plan's credits are managed by your account manager - contact support to top up."}
          </Typography>
        </Box> */}
      </Box>
    </Box>
  );
};

export default CreditBalanceCard;
